<template>
    <div v-if="visible" class="pointer-events-none fixed inset-0 z-[300] flex items-end justify-end p-4 sm:p-6">
        <!-- Panel -->
        <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="translate-y-3 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-3 opacity-0"
        >
            <div
                v-if="open"
                class="pointer-events-auto mb-16 flex max-h-[min(38rem,calc(100vh-8rem))] w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
            >
                <div class="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
                    <div class="flex items-center gap-2">
                        <SparklesIcon class="size-5 text-indigo-600" aria-hidden="true" />
                        <span class="text-sm font-semibold text-gray-900">Ask Inventree</span>
                    </div>
                    <div class="flex items-center gap-0.5">
                        <button
                            v-if="ask.started"
                            type="button"
                            title="Start a new conversation"
                            class="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            @click="ask.newChat()"
                        >
                            <PencilSquareIcon class="size-4" aria-hidden="true" />
                            <span class="sr-only">Start a new conversation</span>
                        </button>
                        <router-link
                            :to="{ name: 'ask' }"
                            title="Open the full page"
                            class="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            @click="open = false"
                        >
                            <ArrowsPointingOutIcon class="size-4" aria-hidden="true" />
                            <span class="sr-only">Open the full page</span>
                        </router-link>
                        <button
                            type="button"
                            title="Close"
                            class="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                            @click="open = false"
                        >
                            <XMarkIcon class="size-4" aria-hidden="true" />
                            <span class="sr-only">Close</span>
                        </button>
                    </div>
                </div>

                <div ref="scrollRef" class="min-h-0 flex-1 overflow-y-auto">
                    <div v-if="ask.started">
                        <div v-for="turn in ask.turns" :key="turn.id" class="border-b border-gray-100">
                            <AskAnswer :ask="ask" :turn="turn" compact @navigated="open = false" />
                        </div>
                    </div>

                    <div v-else class="px-4 py-5">
                        <p class="text-sm text-gray-600">Ask about your stock, sales, customers or figures in plain language.</p>

                        <div v-if="ask.suggestions.length" class="mt-3 flex flex-col items-start gap-1.5">
                            <button
                                v-for="s in ask.suggestions"
                                :key="s"
                                type="button"
                                class="rounded-lg border border-gray-200 px-2.5 py-1.5 text-left text-xs text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                                @click="ask.askThis(s)"
                            >
                                {{ s }}
                            </button>
                        </div>
                    </div>

                    <!-- Only on an empty panel. Mid-conversation the thread is
                         what the space is for, and a list of other threads
                         under it is an invitation to lose your place. -->
                    <div v-if="!ask.started && ask.history.length" class="px-4 py-3">
                        <p class="text-[11px] font-medium tracking-wide text-gray-400 uppercase">Earlier</p>
                        <ul class="mt-1.5 space-y-0.5">
                            <li v-for="item in ask.history.slice(0, 5)" :key="item.conversation_id" class="group flex items-center gap-1.5">
                                <button
                                    type="button"
                                    class="min-w-0 flex-1 truncate rounded px-1.5 py-1 text-left text-xs text-gray-600 hover:bg-gray-50 hover:text-indigo-600"
                                    @click="ask.show(item)"
                                >
                                    {{ item.question }}
                                </button>
                                <button
                                    type="button"
                                    title="Delete this conversation"
                                    class="shrink-0 rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-600 group-hover:text-gray-400"
                                    @click.stop="ask.remove(item)"
                                >
                                    <TrashIcon class="size-3.5" aria-hidden="true" />
                                    <span class="sr-only">Delete this conversation</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="shrink-0 border-t border-gray-100 bg-white px-3 py-3">
                    <AskComposer :ask="ask" :placeholder="ask.started ? 'Ask a follow-up…' : 'Ask a question…'" />
                    <p v-if="ask.errorMessage" class="mt-2 text-xs text-red-700">{{ ask.errorMessage }}</p>
                </div>
            </div>
        </Transition>

        <!-- The button itself, pinned bottom-right whatever the panel is doing. -->
        <button
            type="button"
            :title="open ? 'Close Ask Inventree' : 'Ask Inventree'"
            class="pointer-events-auto absolute right-4 bottom-4 flex size-13 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:outline-none sm:right-6 sm:bottom-6"
            @click="toggle"
        >
            <XMarkIcon v-if="open" class="size-6" aria-hidden="true" />
            <SparklesIcon v-else class="size-6" aria-hidden="true" />
            <span class="sr-only">{{ open ? 'Close Ask Inventree' : 'Ask Inventree' }}</span>
        </button>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ArrowsPointingOutIcon, PencilSquareIcon, SparklesIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useAsk } from '@/composables/useAsk.js';
import { useAuthStore } from '@/stores/auth-store.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import AskAnswer from '@/components/Portal/Ask/AskAnswer.vue';
import AskComposer from '@/components/Portal/Ask/AskComposer.vue';

/**
 * Ask, from anywhere.
 *
 * Its own useAsk() instance, deliberately separate from the /ask page's: the
 * two are different conversations and sharing one would mean opening the bubble
 * silently replaced whatever the page was showing.
 */
const ask = useAsk();
const auth = useAuthStore();
const configStore = useConfigurationStore();
const route = useRoute();

const open = ref(false);
const scrollRef = ref(null);

/**
 * Whether this person, on this deployment, is hiding the button.
 *
 * `users.ask_launcher` is a tri-state: null inherits the operator's setting,
 * 0 hides it, 1 keeps it. Written out rather than coerced, because Number(null)
 * is 0 and a shortcut here would hide the button for every account that has
 * never opened its settings — which is all of them.
 */
const hiddenByUser = computed(() => {
    const choice = auth.user?.ask_launcher;

    return choice !== null && choice !== undefined && Number(choice) === 0;
});

// Four conditions, and all of them have to hold: the deployment has the addon,
// the operator wants the button here, this person has not put it away, and they
// are actually logged in.
//
// This is now the ONLY way into Ask — it left the sidebar, because a question is
// something you have while looking at something else, and a rail entry that
// navigates away from what prompted it is the wrong shape for that. So the
// operator's switch is no longer cosmetic: with it off, Ask is not reachable.
//
// Still hidden on /ask itself, where a floating shortcut to the page you are on
// is just a thing covering the page you are on.
const visible = computed(
    () =>
        auth.isLoggedIn &&
        configStore.isAddonEnabled('ask_inventree') &&
        configStore.askLauncherEnabled &&
        !hiddenByUser.value &&
        route.name !== 'ask',
);

// Loaded on first open rather than on mount: this component exists on every
// page, and two extra requests per page load to fill a panel nobody has clicked
// is a poor trade.
let loaded = false;

function toggle() {
    open.value = !open.value;

    if (open.value && !loaded) {
        loaded = true;
        ask.loadVocabulary();
        ask.loadHistory();
    }
}

// A fresh turn should be the thing you are looking at, not something you have to
// scroll to find. Down rather than up, because the thread reads oldest first and
// the newest question is at the bottom — and after the DOM has the new turn in
// it, or the height being scrolled to is the previous one's.
watch(
    () => ask.turns.length,
    async () => {
        await nextTick();
        if (scrollRef.value) scrollRef.value.scrollTop = scrollRef.value.scrollHeight;
    },
);
</script>
